// src/components/views/admin/AdminTryoutCapturesView.tsx
/**
 * Admin gallery of real tryout UPLOAD runs — original + AI empty A/B + staged —
 * for reviewing how the AI performs on customers' own photos. Read-only; rows
 * are written by the tryout-generate edge function and pruned after 90 days.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Lightbox } from '@/components/ui/Lightbox';

const PAGE_SIZE = 24;

interface CaptureRow {
  id: string;
  run_id: string;
  room_type: string;
  staging_style: string | null;
  original_path: string;
  empty_a_path: string | null;
  empty_b_path: string | null;
  staged_path: string | null;
  created_at: string;
  tryout_tokens: { crm_lead_company: string | null; crm_lead_email: string | null; name: string | null } | null;
}

const publicUrl = (path: string) => supabase.storage.from('images').getPublicUrl(path).data.publicUrl;

function Tile({ path, label, onOpen }: { path: string | null; label: string; onOpen: (url: string) => void }) {
  return (
    <div className="space-y-1">
      <div className="relative aspect-[4/3] rounded-md border border-border bg-muted/30 overflow-hidden">
        {path ? (
          <img
            src={publicUrl(path)}
            alt={label}
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => onOpen(publicUrl(path))}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">
            —
          </div>
        )}
      </div>
      <p className="text-[11px] font-medium text-center">{label}</p>
    </div>
  );
}

export function AdminTryoutCapturesView() {
  const [rows, setRows] = useState<CaptureRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const fetchPage = useCallback(async (p: number) => {
    const from = p * PAGE_SIZE;
    const { data, error: err } = await supabase
      .from('tryout_upload_captures')
      .select('*, tryout_tokens(crm_lead_company, crm_lead_email, name)')
      .order('created_at', { ascending: false })
      .range(from, from + PAGE_SIZE - 1);
    if (err) throw err;
    const list = (data ?? []) as CaptureRow[];
    setRows(list);
    setHasMore(list.length === PAGE_SIZE);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchPage(page)
      .catch((e) => setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen'))
      .finally(() => setIsLoading(false));
  }, [fetchPage, page]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">KI-Qualität — Kunden-Uploads</h2>
        <p className="text-sm text-muted-foreground">
          Echte im Tryout hochgeladene Fotos mit den KI-Ergebnissen (Leer A/B, Eingerichtet),
          um die KI-Qualität an Kundenbildern zu prüfen. Beispielbilder sind ausgenommen.
          Aufnahmen werden nach 90 Tagen automatisch gelöscht.
        </p>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-12">
          Noch keine Uploads erfasst.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => {
            const lead =
              r.tryout_tokens?.crm_lead_company ||
              r.tryout_tokens?.crm_lead_email ||
              r.tryout_tokens?.name ||
              'Unbekannt';
            return (
              <div key={r.id} className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{lead}</span>
                  <span className="text-muted-foreground">
                    {r.room_type}
                    {r.staging_style ? ` · ${r.staging_style}` : ''} ·{' '}
                    {new Date(r.created_at).toLocaleString('de-DE')}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Tile path={r.original_path} label="Original" onOpen={setLightboxUrl} />
                  <Tile path={r.empty_a_path} label="Leer A" onOpen={setLightboxUrl} />
                  <Tile path={r.empty_b_path} label="Leer B" onOpen={setLightboxUrl} />
                  <Tile path={r.staged_path} label="Eingerichtet" onOpen={setLightboxUrl} />
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 h-9 rounded-md border border-border text-sm disabled:opacity-40"
            >
              Zurück
            </button>
            <span className="text-sm text-muted-foreground">Seite {page + 1}</span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="px-3 h-9 rounded-md border border-border text-sm disabled:opacity-40"
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
